<template>
  <div class="col-12 media-wrapper">
    <div class="media-header p-3">
      <div class="media-toolbar">
        <div class="filter position-relative">
          <button
            type="button"
            class="btn btn-outline-secondary w-100"
            :class="{'active': mediaStore.filters.category_ids.length}"
            @click="categoryFilterOpen = !categoryFilterOpen"
          >
            <Icon icon="fa-pro-solid:filter-list" class="me-2" />
            Kategorien
            <span v-if="mediaStore.filters.category_ids.length" class="badge bg-primary ms-1">
              {{ mediaStore.filters.category_ids.length }}
            </span>
          </button>
          <div v-if="categoryFilterOpen" class="category-filter-overlay position-absolute">
            <div class="p-3">
              <strong class="d-block mb-2">Nach Kategorie filtern</strong>
              <div v-if="!assetCategories.length" class="small text-muted mb-2">Keine Kategorien vorhanden</div>
              <div v-for="assetCategory in assetCategories" :key="assetCategory.id" class="form-check">
                <input
                  :id="`assetCategoryCheckbox${assetCategory.id}`"
                  v-model="mediaStore.filters.category_ids"
                  class="form-check-input"
                  type="checkbox"
                  :value="assetCategory.id"
                >
                <label class="form-check-label" :for="`assetCategoryCheckbox${assetCategory.id}`">
                  {{ assetCategory.name }}
                </label>
              </div>
              <button type="button" class="btn btn-primary btn-sm mt-3" @click="setCategories">Filter anwenden</button>
            </div>
          </div>
        </div>

        <MultiSelect
          v-model="mediaStore.filters.type"
          :options="fileTypes"
          optionLabel="name"
          optionValue="type"
          :disabled="lockTypeSelect"
          display="chip"
          :showToggleAll="false"
          placeholder="Dateityp"
          @change="changeTypeFilter"
        />

        <div class="media-search position-relative">
          <Icon icon="fa-pro-solid:magnifying-glass" class="media-search__icon" />
          <input
            v-model="mediaStore.filters.search"
            type="search"
            class="form-control"
            placeholder="Medien durchsuchen"
            aria-label="Medien durchsuchen"
            @input="textSearch"
          >
        </div>

        <button type="button" class="btn btn-link media-reset" @click="resetFilters" title="Filter zurücksetzen">
          <Icon icon="fa-pro-solid:rotate-left" class="me-1" />
          Zurücksetzen
        </button>
      </div>

      <div class="media-pagination mt-3">
        <span class="small text-muted">{{ mediaStore.total }} Medien</span>
        <div class="d-flex align-items-center gap-2">
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="mediaStore.page <= 1 || loading" @click="previousPage" aria-label="Vorherige Seite">
            <Icon icon="fa-pro-solid:arrow-left" />
          </button>
          <span class="small">Seite {{ mediaStore.page }} von {{ totalPages }}</span>
          <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="mediaStore.page >= totalPages || loading" @click="nextPage" aria-label="Nächste Seite">
            <Icon icon="fa-pro-solid:arrow-right" />
          </button>
        </div>
      </div>
    </div>

    <div class="row media-content g-0" :class="{'media-content--selectable': !editMode}">
      <div v-if="loading" class="media-state">
        <span class="spinner-border text-primary" aria-hidden="true"></span>
        <span>Medien werden geladen ...</span>
      </div>
      <div v-else-if="!mediaStore.assets.length" class="media-state">
        <Icon icon="fa-pro-solid:images" class="media-state__icon" />
        <strong>Keine Medien gefunden</strong>
        <span class="small text-muted">Passen Sie die Suche oder Filter an.</span>
      </div>
      <template v-else>
        <MediaCard
          v-for="asset in mediaStore.assets"
          :key="asset.id"
          :hovered="asset.id === hoveredId"
          :editMode="editMode"
          :asset="asset"
          :editable="editable"
          :selected="selectedAssets.some((item) => item.id === asset.id)"
          class="col-12 col-md-6 col-xl-3 p-2"
          :class="{'hover-pointer': !editMode}"
          :role="editMode ? undefined : 'button'"
          :tabindex="editMode ? undefined : 0"
          @delete="deleteAsset"
          @mouseover="hoverMedia(asset)"
          @mouseleave="hoveredId = null"
          @click="selectMedia(asset)"
          @edit="editMedia"
          @keydown.enter.prevent="selectMedia(asset)"
          @keydown.space.prevent="selectMedia(asset)"
        />
      </template>
    </div>
  </div>
</template>

<script>
import {useUserStore} from "@/stores/user";
import {useMediaStore} from "@/stores/media";
import {useMediaCategoryStore} from "@/stores/media_category";
import {Helpers} from "@/helpers";
import MediaCard from "./MediaCard";
import MultiSelect from 'primevue/multiselect';

export default {
  name: "MediaList",
  components: {MediaCard, MultiSelect},
  props: {
    editMode: {type: Boolean, default: false},
    selectedAssets: {type: Array, default: () => []},
    lockTypeSelect: {type: Boolean, default: false},
    editable: {type: Boolean, default: false},
  },
  emits: ['mediaHovered', 'mediaSelected', 'mediaEdit'],
  setup() {
    return {
      userStore: useUserStore(),
      mediaStore: useMediaStore(),
      mediaCategoryStore: useMediaCategoryStore(),
    }
  },
  data() {
    return {
      hoveredId: null,
      categoryFilterOpen: false,
      assetCategories: [],
      timeout: null,
      loading: false,
      fileTypes: [
        {type: "images", name: "Bilder"},
        {type: "documents", name: "Dokumente"},
      ],
    }
  },
  computed: {
    totalPages() {
      return Math.max(Math.ceil(this.mediaStore.total / this.mediaStore.size), 1)
    },
  },
  created() {
    if (this.lockTypeSelect) this.mediaStore.filters.type = ['images']
    this.fetchAssets()
    this.mediaCategoryStore.index(this.userStore.user.organisation.kdnr).then(({data}) => {
      this.assetCategories = data
    })
  },
  beforeUnmount() {
    clearTimeout(this.timeout)
  },
  methods: {
    async fetchAssets() {
      this.loading = true
      try {
        await this.mediaStore.getAssets(this.userStore.user.organisation.kdnr)
      } finally {
        this.loading = false
      }
    },
    changeTypeFilter() {
      this.mediaStore.page = 1
      this.fetchAssets()
    },
    textSearch() {
      clearTimeout(this.timeout)
      this.mediaStore.page = 1
      this.timeout = setTimeout(() => this.fetchAssets(), 300)
    },
    previousPage() {
      this.mediaStore.page = Math.max(this.mediaStore.page - 1, 1)
      this.fetchAssets()
    },
    nextPage() {
      this.mediaStore.page = Math.min(this.mediaStore.page + 1, this.totalPages)
      this.fetchAssets()
    },
    hoverMedia(asset) {
      this.hoveredId = asset.id
      this.$emit('mediaHovered', asset)
    },
    selectMedia(asset) {
      if (!this.editMode) this.$emit('mediaSelected', asset)
    },
    editMedia(asset) {
      this.$emit('mediaEdit', asset)
    },
    async deleteAsset(id) {
      try {
        await this.mediaStore.deleteAsset(id, this.userStore.user.organisation.kdnr)
        Helpers.emitter.emit('success', 'Medium wurde aus der zentralen Mediathek entfernt.')
      } catch (error) {
        const response = error.response?.data
        if (response?.error === 'asset_in_use') {
          const count = response.usages?.length || 0
          Helpers.emitter.emit('error', `Das Medium wird noch verwendet (${count} Verwendung${count === 1 ? '' : 'en'}).`)
        } else if (response?.error === 'basis3_unavailable') {
          Helpers.emitter.emit('error', 'Die Verwendung in Basis3 konnte nicht geprüft werden. Bitte später erneut versuchen.')
        } else {
          Helpers.emitter.emit('error', response?.message || error.message || 'Medium konnte nicht gelöscht werden.')
        }
      }
    },
    setCategories() {
      this.categoryFilterOpen = false
      this.mediaStore.page = 1
      this.fetchAssets()
    },
    resetFilters() {
      this.mediaStore.filters = {
        category_ids: [],
        search: '',
        type: this.lockTypeSelect ? ['images'] : [],
      }
      this.mediaStore.page = 1
      this.fetchAssets()
    },
  },
}
</script>

<style scoped lang="scss">
@import "src/scss/constants";

.media-header,
.media-content {
  background: white;
}

.media-header {
  border-bottom: 1px solid #dce4ea;
}

.media-toolbar {
  display: grid;
  grid-template-columns: auto minmax(180px, 240px) minmax(220px, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
}

.category-filter-overlay {
  min-width: 300px;
  z-index: 10;
  top: calc(100% + 0.35rem);
  left: 0;
  background: white;
  border: 1px solid #ccd7df;
  border-radius: 6px;
  box-shadow: 0 0.5rem 1.5rem rgba(28, 54, 74, 0.16);
}

.media-search__icon {
  position: absolute;
  left: 0.8rem;
  top: 50%;
  z-index: 1;
  color: #728696;
  transform: translateY(-50%);
}

.media-search input {
  padding-left: 2.3rem;
}

.media-reset {
  white-space: nowrap;
}

.media-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.media-content {
  min-height: 230px;
}

.media-content--selectable :deep(.media-card) {
  border: 2px solid transparent;
  border-radius: 7px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.media-content--selectable :deep(.media-card:hover),
.media-content--selectable :deep(.media-card:focus) {
  border-color: #79a9c4;
  box-shadow: 0 0 0 2px rgba(27, 108, 151, 0.1);
  outline: none;
}

.media-state {
  min-height: 230px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #51697c;
}

.media-state__icon {
  font-size: 2rem;
  color: #8aa0b1;
}

@media (max-width: 991.98px) {
  .media-toolbar {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 575.98px) {
  .media-toolbar {
    grid-template-columns: 1fr;
  }

  .category-filter-overlay {
    min-width: min(300px, 88vw);
  }

  .media-pagination {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
